module Client.Page.User.ReceivedSensorPackets

open Browser.Types
open Client.Components
open Client.Domain
open Client.DomainTypes.Msg
open Client.Infrastructure.Api
open Client.InfrastructureTypes
open Client.Map.Symbol
open Elmish
open Fable.FontAwesome
open Fable.React
open Fable.React.Props
open Fulma
open Shared.Dto.Dto
open Shared.Dto.Page.User.SensorDataPackets
open Shared.Dto.Sensor
open Shared.Infrastructure

let private signalStrengthCls = "signal-strength"

type DataModel = {
    Session: UserSession
    Name: SensorNameDto
    Packets: SensorPacketDto list
    IsRequestRunning: bool
}

type LoadingModel = {
    Session: UserSession
    SensorId: int
    Name: SensorNameDto option
    Packets: SensorPacketDto list option
}

type Model = Loadable<DataModel, LoadingModel>

let private loadPacketsCmd (key: SessionKey) (request: LoadSensorPacketsRequest) =
    let requestData = {
        SessionKey = key
        Data = request
    }

    Cmd.OfAsync.perform
        api.getReceivedSensorPackets
        requestData
        (SensorDataPacketsMsg.DataReceived >> Msg.ReceivedSensorPackets)

let private loadSensorNameCmd (key: SessionKey) (sensorId: int) =
    let requestData = {
        SessionKey = key
        Data = sensorId
    }

    Cmd.OfAsync.perform api.getSensorName requestData (NameReceived >> Msg.ReceivedSensorPackets)

let init (sensorId: int) (session: UserSession) =
    let cmd =
        Cmd.batch [
            loadSensorNameCmd session.SessionKey sensorId
            loadPacketsCmd session.SessionKey {
                Pagination = {
                    Offset = 0
                    Limit = 10
                }
                SensorId = sensorId
            }
        ]

    Loadable.Loading {
        Session = session
        SensorId = sensorId
        Name = None
        Packets = None
    },
    cmd

let private createDataModel (loading: LoadingModel) =
    Option.map2
        (fun name packets -> {
            Session = loading.Session
            Name = name
            Packets = packets
            IsRequestRunning = false
        })
        loading.Name
        loading.Packets
    |> Option.map Loadable.Data
    |> Option.defaultValue (Loadable.Loading loading)

let update (msg: SensorDataPacketsMsg) (model: Model) =
    match msg, model with
    | SensorDataPacketsMsg.DataReceived response, Loadable.Loading loading ->
        match response with
        | Ok packets -> createDataModel { loading with Packets = Some packets }, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | SensorDataPacketsMsg.NameReceived response, Loadable.Loading loading ->
        match response with
        | Ok name -> createDataModel { loading with Name = Some name }, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | SensorDataPacketsMsg.DataReceived response, Loadable.Data data ->
        match response with
        | Ok packets ->
            let newPackets =
                packets @ data.Packets
                |> List.sortByDescending (fun packet -> packet.ReceivedAt)

            Loadable.Data {
                data with
                    Packets = newPackets
                    IsRequestRunning = false
            },
            Cmd.none
        | Result.Error err ->
            let cmd = ErrorConfirmation.responseError ErrorConfirmation.defaultConfig err

            Loadable.Data { data with IsRequestRunning = false }, cmd
    | SensorDataPacketsMsg.LoadMoreData request, Loadable.Data data ->
        Loadable.Data { data with IsRequestRunning = true }, loadPacketsCmd data.Session.SessionKey request
    | _ -> model, Cmd.none

let receiverRow (receiver: ReceiverDto) =
    let receiverName =
        match receiver.ReceiverType with
        | MyGateway gateway ->
            span [
                Style [ FontWeight "bold"; TextDecoration "underline" ]
            ] [ str gateway.Name ]
        | MySensGateway -> span [] [ str "MySens Gateway" ]
        | UnknownGateway -> span [ Style [ Color "#95A1AC" ] ] [ str "Unbekannter Gateway" ]


    let signalStrength = receiver.Rssi |> SignalStrength.fromRssi

    let imgSrc = signalStrength |> SignalStrength.toImageSrc

    let signalStrengthText = signalStrength |> SignalStrength.toString

    tr [] [
        td [] [
            div [ Class "with-icon-left" ] [
                Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.BroadcastTower ] [] ]

                receiverName
            ]
        ]
        td [] [
            div [ Class "with-icon-left" ] [
                img [ Style [ Width "30px" ]; Src imgSrc ]

                span [] [ str signalStrengthText ]
            ]
        ]
    ]

let receiversView (receivers: ReceiverDto list) =
    Table.table [ Table.IsFullWidth ] [
        thead [] [
            tr [] [
                th [] [ str "Empfänger" ]
                th [] [ str "Empfangsqualität" ]
            ]
        ]
        tbody [] (List.map receiverRow receivers)
    ]

let packetView (idx: int) (packet: SensorPacketDto) =
    let elapsedTime =
        packet.ReceivedAt
        |> TimeSpan.createElapsedTime
        |> TimeSpan.toHumanReadable
        |> sprintf "vor %s"

    let receivedTime =
        packet.ReceivedAt |> DateTimeOffset.toLocalDateTime |> DateTime.toShortString

    Box.box' [ GenericOption.CustomClass "grey_box" ] [
        Heading.h3 [] [ str (sprintf "Datenpaket #%i" (idx + 1)) ]
        Table.table [ Table.IsFullWidth ] [
            tbody [] [
                tr [] [
                    td [] [
                        div [ Class "with-icon-left" ] [
                            Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.Clock ] [] ]

                            span [] [
                                str (sprintf "%s (%s)" receivedTime elapsedTime)
                            ]
                        ]
                    ]
                ]
                tr [] [ td [] [ receiversView packet.Receivers ] ]
            ]
        ]
    ]

let private listView (packets: SensorPacketDto list) =
    match packets with
    | [] -> [
        Heading.h1 [] [
            str "Es wurden keine Datenpakete zu diesem Sensor gefunden."
        ]
      ]
    | _ -> List.mapi packetView packets

let private heading (name: SensorNameDto) = [
    Heading.h1 [ Heading.CustomClass "has-text-centered" ] [ str name.MapSensorName ]
    match name.PhysicalSensorName with
    | Some physicalSensorName ->
        Heading.h3 [
            Heading.IsSubtitle
            Heading.CustomClass "has-text-centered"
        ] [ str physicalSensorName ]
    | None -> ()
]

let onLoadMoreClick dispatch (model: DataModel) (event: MouseEvent) =
    let request = {
        SensorId = model.Name.Id
        Pagination = {
            Limit = 10
            Offset = model.Packets.Length
        }
    }

    LoadMoreData request |> Msg.ReceivedSensorPackets |> dispatch


let private loadMoreButton dispatch (model: DataModel) =
    let f = onLoadMoreClick dispatch model

    Pagination.createLoadMoreButton model.IsRequestRunning "10 weitere Datenpakete laden" f

let dataView dispatch (model: DataModel) =
    Container.container [
        Container.isFullWidth
        Container.CustomClass "flex-container"
    ] [
        yield! heading model.Name

        yield! listView model.Packets

        loadMoreButton dispatch model
    ]

let view dispatch (model: Model) = Loadable.view (dataView dispatch) model